import { motion, useInView } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { ArrowRight, Sparkles } from 'lucide-react';
import { useSnapshot } from '../../context/SnapshotContext';
import { useCountUp } from '../../hooks/useCountUp';

const ease = [0.22, 1, 0.36, 1];

export function Hero() {
    const { openSnapshot } = useSnapshot();
    return (
        <section
            data-testid="hero-section"
            className="relative min-h-[100vh] pt-28 pb-20 overflow-hidden"
        >
            <div className="absolute inset-0 grid-bg grid-bg-fade opacity-50" />
            <div className="atmosphere" />

            <div className="shell relative grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-16 items-center min-h-[calc(100vh-7rem)]">
                {/* LEFT — Copy */}
                <div className="relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease }}
                        className="inline-flex items-center gap-2.5 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.03] backdrop-blur-md"
                    >
                        <span className="relative flex w-1.5 h-1.5">
                            <span className="absolute inset-0 rounded-full bg-cyan-400 pulse-dot" />
                            <span className="relative inline-flex rounded-full w-1.5 h-1.5 bg-cyan-400" />
                        </span>
                        <span className="font-mono text-[10.5px] tracking-[0.2em] uppercase text-slate-300">
                            Growth Systems Studio · Est. 2020
                        </span>
                    </motion.div>

                    <motion.h1
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.1, ease }}
                        className="mt-7 font-display font-semibold text-[2.6rem] sm:text-[3.6rem] lg:text-[5rem] leading-[0.96] tracking-[-0.045em]"
                    >
                        <span className="text-white">We engineer the system</span>
                        <br />
                        <span className="text-gradient-cyan">between attention</span>
                        <br />
                        <span className="text-gradient">and a booked client.</span>
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.25, ease }}
                        className="mt-7 max-w-xl text-[17px] sm:text-[18px] leading-[1.65] text-slate-300"
                    >
                        Websites, custom apps and AI follow-up — wired into one
                        growth system that captures, qualifies and routes every
                        lead. Built for clinics and serious service businesses.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.4, ease }}
                        className="mt-10 flex flex-wrap items-center gap-3.5"
                    >
                        <button
                            type="button"
                            onClick={openSnapshot}
                            data-testid="hero-cta-snapshot"
                            className="btn btn-primary"
                        >
                            <Sparkles className="w-4 h-4" />
                            Find Where You're Losing Clients
                            <ArrowRight className="w-4 h-4" />
                        </button>
                        <a
                            href="#featured-systems"
                            data-testid="hero-cta-view-systems"
                            className="btn btn-ghost"
                        >
                            View featured systems
                            <ArrowRight className="w-3.5 h-3.5" />
                        </a>
                    </motion.div>

                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.7, delay: 0.55 }}
                        className="mt-5 font-mono text-[10.5px] tracking-[0.22em] uppercase text-slate-500"
                    >
                        2-min diagnostic · Instant clarity · No call required
                    </motion.p>
                </div>

                {/* RIGHT — Cinematic system architecture */}
                <SystemArchitecture />
            </div>

            {/* Bottom hairline + scroll cue */}
            <div className="shell relative mt-20">
                <div className="hr-dim" />
                <div className="flex items-center justify-between mt-5 font-mono text-[10px] tracking-[0.24em] uppercase text-slate-500">
                    <span>FIG. 01 · GROWTH-SYSTEM ARCHITECTURE</span>
                    <span>SCROLL ↓</span>
                </div>
            </div>
        </section>
    );
}

function SystemArchitecture() {
    const containerRef = useRef(null);
    const inView = useInView(containerRef, { once: true, amount: 0.3 });
    const [mouse, setMouse] = useState({ x: 0, y: 0 });

    const handleMove = (e) => {
        const r = containerRef.current?.getBoundingClientRect();
        if (!r) return;
        // -1 .. 1 normalized from center
        const x = ((e.clientX - r.left) / r.width - 0.5) * 2;
        const y = ((e.clientY - r.top) / r.height - 0.5) * 2;
        setMouse({ x, y });
    };
    const handleLeave = () => setMouse({ x: 0, y: 0 });

    // Each card uses a different multiplier so they parallax at different depths
    const px = (mult) => `${mouse.x * mult}px`;
    const py = (mult) => `${mouse.y * mult}px`;

    return (
        <div
            ref={containerRef}
            onMouseMove={handleMove}
            onMouseLeave={handleLeave}
            className="relative w-full aspect-[4/4.4] sm:aspect-[5/5] lg:aspect-[1/1] mx-auto max-w-[560px]"
        >
            {/* Backplate atmospheric glow */}
            <div
                className="absolute inset-0 rounded-[2rem] opacity-90"
                style={{
                    background:
                        'radial-gradient(ellipse 70% 60% at 50% 40%, rgba(6, 182, 212, 0.18), transparent 65%), radial-gradient(ellipse 50% 80% at 50% 100%, rgba(139, 92, 246, 0.18), transparent 70%)',
                }}
            />

            {/* Connecting flow SVG (behind cards) */}
            <svg
                viewBox="0 0 400 400"
                className="absolute inset-0 w-full h-full"
                style={{ filter: 'drop-shadow(0 0 12px rgba(6, 182, 212, 0.4))' }}
                aria-hidden
            >
                <defs>
                    <linearGradient id="flowA" x1="0" x2="1" y1="0" y2="1">
                        <stop offset="0%" stopColor="#06B6D4" />
                        <stop offset="100%" stopColor="#3B82F6" />
                    </linearGradient>
                    <linearGradient id="flowB" x1="0" x2="1" y1="0" y2="1">
                        <stop offset="0%" stopColor="#3B82F6" />
                        <stop offset="100%" stopColor="#8B5CF6" />
                    </linearGradient>
                </defs>
                <path
                    d="M 80 90 Q 140 140 200 170 Q 270 200 320 240"
                    fill="none"
                    stroke="url(#flowA)"
                    strokeWidth="1.5"
                    className="flow-path"
                    opacity="0.85"
                />
                <path
                    d="M 320 90 Q 240 150 200 200 Q 160 250 80 310"
                    fill="none"
                    stroke="url(#flowB)"
                    strokeWidth="1.5"
                    className="flow-path"
                    opacity="0.7"
                    style={{ animationDelay: '1s' }}
                />
            </svg>

            {/* Card 1 — Traffic source */}
            <FloatingCard
                className="absolute left-[2%] top-[6%] w-[44%] drift"
                delay={0.2}
                tilt="-6deg"
            >
                <CardLabel>01 · TRAFFIC</CardLabel>
                <div className="mt-3 grid grid-cols-2 gap-2">
                    {['ADS', 'SEO', 'SOCIAL', 'REF'].map((t) => (
                        <div
                            key={t}
                            className="rounded-md py-1.5 text-center font-mono text-[9px] tracking-[0.18em] text-slate-300"
                            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
                        >
                            {t}
                        </div>
                    ))}
                </div>
            </FloatingCard>

            {/* Card 2 — Qualify (CENTER, primary) */}
            <FloatingCard
                className="absolute left-1/2 top-[34%] -translate-x-1/2 w-[58%] z-10"
                delay={0.5}
                tilt="0deg"
                primary
                offset={{ x: px(-22), y: py(-10) }}
            >
                <CardLabel cyan>02 · QUALIFY · AI</CardLabel>
                <p className="mt-3 font-display text-[15px] text-white tracking-[-0.02em]">
                    Diagnose · Capture · Score
                </p>
                <div className="mt-3 space-y-1.5">
                    {[
                        { l: 'Intent', v: 86 },
                        { l: 'Fit', v: 72 },
                        { l: 'Urgency', v: 58 },
                    ].map((row, i) => (
                        <ScoreBar key={row.l} label={row.l} target={row.v} delay={i * 220} active={inView} />
                    ))}
                </div>
            </FloatingCard>

            {/* Card 3 — Follow-up */}
            <FloatingCard
                className="absolute right-[2%] top-[8%] w-[42%] drift-slow"
                delay={0.35}
                tilt="6deg"
                offset={{ x: px(14), y: py(-7) }}
            >
                <CardLabel>03 · FOLLOW-UP</CardLabel>
                <div className="mt-3 space-y-1.5">
                    {[
                        { t: '0:02', m: 'AI replies' },
                        { t: '0:08', m: 'Calendar sent' },
                        { t: '1:15', m: 'Confirmed' },
                    ].map((r) => (
                        <div key={r.t} className="flex items-center justify-between text-[10px]">
                            <span className="font-mono tracking-[0.14em] text-cyan-400">{r.t}</span>
                            <span className="text-slate-300">{r.m}</span>
                        </div>
                    ))}
                </div>
            </FloatingCard>

            {/* Card 4 — Booked outcome */}
            <FloatingCard
                className="absolute left-[4%] bottom-[6%] w-[48%] drift"
                delay={0.65}
                tilt="-3deg"
                outcome
                offset={{ x: px(-16), y: py(8) }}
            >
                <CardLabel cyan>04 · BOOKED</CardLabel>
                <BookedNumber active={inView} />
                <p className="mt-1 text-[11px] text-slate-400">consultations / 30 days</p>
                <div className="mt-3 h-[26px] flex items-end gap-1">
                    {[24, 38, 32, 56, 48, 72, 64, 88, 76, 92].map((v, i) => (
                        <motion.div
                            key={i}
                            className="flex-1 rounded-sm origin-bottom"
                            initial={{ scaleY: 0 }}
                            animate={inView ? { scaleY: 1 } : {}}
                            transition={{ duration: 0.7, delay: 0.9 + i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                            style={{
                                height: `${v}%`,
                                background: 'linear-gradient(180deg, #06B6D4, #3B82F6)',
                                opacity: 0.4 + (i / 18),
                            }}
                        />
                    ))}
                </div>
            </FloatingCard>

            {/* Card 5 — Telemetry */}
            <FloatingCard
                className="absolute right-[5%] bottom-[10%] w-[40%] drift-slow"
                delay={0.5}
                tilt="3deg"
                offset={{ x: px(18), y: py(10) }}
            >
                <CardLabel>05 · TELEMETRY</CardLabel>
                <TelemetryRow active={inView} />
            </FloatingCard>
        </div>
    );
}

function FloatingCard({ children, className, delay = 0, tilt = '0deg', primary, outcome }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 24, rotateX: -6 }}
            animate={{ opacity: 1, y: 0, rotateX: 0 }}
            transition={{ duration: 0.9, delay, ease }}
            className={className}
            style={{
                transform: `perspective(1200px) rotateY(${tilt})`,
                transformStyle: 'preserve-3d',
            }}
        >
            <div
                className="rounded-2xl p-3.5 backdrop-blur-xl"
                style={{
                    background: primary
                        ? 'linear-gradient(145deg, rgba(6,182,212,0.12), rgba(59,130,246,0.06))'
                        : outcome
                            ? 'linear-gradient(145deg, rgba(139,92,246,0.10), rgba(11,18,33,0.6))'
                            : 'rgba(11, 18, 33, 0.55)',
                    border: primary ? '1px solid rgba(6,182,212,0.4)' : '1px solid rgba(255,255,255,0.10)',
                    boxShadow: primary
                        ? '0 22px 50px -16px rgba(6, 182, 212, 0.45), 0 0 0 1px rgba(255,255,255,0.04) inset'
                        : '0 22px 50px -22px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.04) inset',
                }}
            >
                {children}
            </div>
        </motion.div>
    );
}

function CardLabel({ children, cyan }) {
    return (
        <p
            className={`font-mono text-[9.5px] tracking-[0.22em] uppercase font-medium ${cyan ? 'text-cyan-400' : 'text-slate-400'}`}
        >
            {children}
        </p>
    );
}

function ScoreBar({ label, target, delay = 0, active }) {
    const [v, setV] = useState(0);
    useEffect(() => {
        if (!active) return;
        const t = setTimeout(() => {
            const start = performance.now();
            const dur = 1200;
            const tick = (now) => {
                const k = Math.min(1, (now - start) / dur);
                const eased = 1 - Math.pow(1 - k, 3);
                setV(Math.round(eased * target));
                if (k < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
        }, delay + 600);
        return () => clearTimeout(t);
    }, [active, target, delay]);
    return (
        <div className="flex items-center gap-2">
            <span className="font-mono text-[9px] tracking-[0.16em] uppercase text-slate-400 w-12">{label}</span>
            <div className="flex-1 h-[3px] rounded-full bg-white/[0.06] overflow-hidden">
                <div
                    className="h-full rounded-full transition-[width] duration-150"
                    style={{
                        width: `${v}%`,
                        background: 'linear-gradient(90deg, #06B6D4, #3B82F6)',
                    }}
                />
            </div>
            <span className="font-mono text-[9px] text-slate-300 w-7 text-right tabular-nums">{v}</span>
        </div>
    );
}

function BookedNumber({ active }) {
    const { ref, value } = useCountUp(127, { duration: 1600 });
    useEffect(() => {
        if (active && ref.current) {
            ref.current.setAttribute('data-active', 'true');
        }
    }, [active, ref]);
    return (
        <p
            ref={ref}
            className="mt-2 font-display font-semibold text-[28px] tracking-[-0.03em] text-white leading-none tabular-nums"
        >
            +{Math.round(value)}
        </p>
    );
}

function TelemetryRow({ active }) {
    const [conv, setConv] = useState(0);
    const [sla, setSla] = useState(120);
    useEffect(() => {
        if (!active) return;
        // Conv counter to 33
        const t1 = setTimeout(() => {
            const start = performance.now();
            const tick = (now) => {
                const k = Math.min(1, (now - start) / 1400);
                const eased = 1 - Math.pow(1 - k, 3);
                setConv(Math.round(eased * 33));
                if (k < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
        }, 700);
        // SLA mini ticker
        const itv = setInterval(() => {
            setSla((prev) => {
                const next = prev + (Math.random() < 0.5 ? -1 : 1) * 4;
                return Math.max(85, Math.min(135, next));
            });
        }, 1800);
        return () => { clearTimeout(t1); clearInterval(itv); };
    }, [active]);
    return (
        <div className="mt-3 space-y-2">
            <div className="flex items-baseline justify-between">
                <span className="font-mono text-[9px] tracking-[0.16em] uppercase text-slate-400">CONV.</span>
                <span className="font-display text-[18px] text-white tracking-[-0.02em] tabular-nums">{conv}%</span>
            </div>
            <div className="flex items-baseline justify-between">
                <span className="font-mono text-[9px] tracking-[0.16em] uppercase text-slate-400">SLA</span>
                <span className="font-mono text-[10px] text-cyan-400 tabular-nums inline-flex items-center gap-1">
                    <span className="w-1 h-1 rounded-full bg-cyan-400 pulse-dot" />
                    {sla}s
                </span>
            </div>
        </div>
    );
}
